import {DeviceLogCategory, LogsService} from "../../../api";

type Writer = {write: (data: Blob) => Promise<void>; close: () => Promise<void>; abort: () => Promise<void>};
type SavePicker = (options: {suggestedName: string}) => Promise<{createWritable: () => Promise<Writer>}>;
type ExportWindow = Window & {showSaveFilePicker?: SavePicker};
export type ExportControl = {stop: boolean; discard: boolean; request?: {cancel: () => void}};
export const supportsDirectSave = () => typeof (window as ExportWindow).showSaveFilePicker === "function";

export type ExportOptions = {
  deviceId: string; from?: string; to?: string; search?: string; category: DeviceLogCategory;
  snapshotAt?: string; limit?: number; total?: number | null;
  control: ExportControl; onProgress: (count: number) => void;
};

export async function exportDeviceLogs(options: ExportOptions) {
  const {control, limit} = options;
  const picker = (window as ExportWindow).showSaveFilePicker;
  const directSave = picker && (limit ?? options.total ?? Infinity) > 10000;
  if (limit !== undefined && (!Number.isSafeInteger(limit) || limit < 1 || limit > 5000000))
    throw new Error("Укажите целое количество от 1 до 5 000 000.");
  if (!picker && (limit ?? options.total ?? Infinity) > 50000)
    throw new Error("В этом браузере выберите не больше 50 000 записей или ограничьте период. Большая выгрузка напрямую в файл поддерживается в настольных браузерах Chromium.");
  const filename = `logs-${options.deviceId}-${new Date().toISOString().slice(0, 10)}`;
  let writer: Writer | undefined;
  let count = 0;
  let bytes = 0;
  const parts: Blob[] = [];
  let at = options.snapshotAt;
  let time: string | undefined;
  let id: string | undefined;
  let exhausted = false;
  try {
    if (directSave && picker) {
      try {
        const handle = await picker.call(window, {suggestedName: `${filename}.jsonl`});
        writer = await handle.createWritable();
      } catch (error) {
        if ((error as {name?: string})?.name === "AbortError") return {count: 0, cancelled: true, stopped: false, exhausted: false};
        throw error;
      }
    }
    while (!control.stop && !control.discard && (limit === undefined || count < limit)) {
      const request = LogsService.getApiLogsPage(options.deviceId, Math.min(5000, limit === undefined ? 5000 : limit - count),
        options.from, options.to, options.search, at, time, id, options.category, false);
      control.request = request;
      let result;
      try { result = await request; } catch (error) {
        if (control.stop || control.discard) break;
        throw error;
      }
      if (control.stop || control.discard) break;
      at = result.snapshotAt;
      const logs = (result.logs ?? []).slice(0, limit === undefined ? undefined : limit - count);
      if (logs.length) {
        const chunk = new Blob([logs.map(log => JSON.stringify({
          id: log.id, time: log.insertDateTime, title: log.title, explanation: log.detail, original: log.data
        })).join("\n") + "\n"], {type: "application/x-ndjson"});
        bytes += chunk.size;
        if (writer) await writer.write(chunk);
        else {
          if (count + logs.length > 50000) throw new Error("Достигнут предел обычного скачивания: 50 000 записей. Уменьшите период или используйте запись напрямую в файл.");
          if (bytes > 64 * 1024 * 1024) throw new Error("Файл превышает 64 МБ. Уменьшите число записей или период, либо используйте браузер с записью напрямую в файл.");
          parts.push(chunk);
        }
        count += logs.length;
        options.onProgress(count);
      }
      if (!result.hasMore) { exhausted = true; break; }
      if (!result.nextTime || !result.nextId || result.nextId === id || logs.length === 0)
        throw new Error("Сервер не вернул корректное продолжение истории.");
      time = result.nextTime;
      id = result.nextId;
    }
    if (control.discard || (control.stop && count === 0)) {
      await writer?.abort();
      writer = undefined;
      return {count: 0, cancelled: true, stopped: false, exhausted: false};
    }
    // A final metadata line distinguishes an intentionally stopped file from a completed selection.
    const footer = new Blob([JSON.stringify({_export: {
      complete: !control.stop, stopped: control.stop, records: count, requestedLimit: limit ?? null,
      snapshotAt: at, category: options.category, from: options.from, to: options.to, search: options.search
    }}) + "\n"], {type: "application/x-ndjson"});
    if (writer) {
      await writer.write(footer);
      await writer.close();
      writer = undefined;
    } else {
      parts.push(footer);
      const url = URL.createObjectURL(new Blob(parts, {type: "application/x-ndjson;charset=utf-8"}));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}${control.stop ? ".partial" : ""}.jsonl`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    }
    return {count, stopped: control.stop, exhausted, cancelled: false};
  } catch (error) {
    await writer?.abort().catch(() => {});
    throw error;
  } finally {
    control.request = undefined;
  }
}
